/**
 * Hook pour gérer l'état du tutoriel interactif de création de garantie
 * Utilise useReducer pour la logique d'état (pas de cascade de useEffect)
 *
 * L'affichage du tutoriel est contrôlé par le champ showTutorials du profil utilisateur.
 * Les flags hasBeenCompleted/hasBeenSkipped ne sont valables que pour la session en cours
 * (pas de persistance localStorage) — le tutoriel se ré-affiche à chaque visite du wizard
 * tant que l'utilisateur n'a pas désactivé les tutoriels dans son profil.
 */

import { useReducer, useCallback } from 'react';

export interface TutorialState {
  isActive: boolean;
  currentStepIndex: number;
  isComplete: boolean;
  hasBeenCompleted: boolean; // tutoriel terminé entièrement (toutes les étapes)
  hasBeenSkipped: boolean; // tutoriel passé/skippé (pas terminé entièrement)
  showIntro: boolean; // afficher la modal d'introduction
}

type TutorialAction =
  | { type: 'START_TUTORIAL'; stepIndex?: number }
  | { type: 'NEXT_STEP' }
  | { type: 'PREVIOUS_STEP' }
  | { type: 'SKIP_TUTORIAL' }
  | { type: 'COMPLETE_TUTORIAL' }
  | { type: 'RESTART_TUTORIAL' }
  | { type: 'RESTART_AT_STEP'; stepIndex: number }
  | { type: 'SHOW_INTRO' }
  | { type: 'HIDE_INTRO' };

const initialState: TutorialState = {
  isActive: false,
  currentStepIndex: 0,
  isComplete: false,
  hasBeenCompleted: false,
  hasBeenSkipped: false,
  showIntro: false,
};

function tutorialReducer(state: TutorialState, action: TutorialAction): TutorialState {
  switch (action.type) {
    case 'SHOW_INTRO':
      return {
        ...state,
        showIntro: true,
      };

    case 'HIDE_INTRO':
      return {
        ...state,
        showIntro: false,
      };

    case 'START_TUTORIAL':
      return {
        ...state,
        isActive: true,
        currentStepIndex: action.stepIndex ?? 0,
        isComplete: false,
        showIntro: false,
      };

    case 'NEXT_STEP':
      return {
        ...state,
        currentStepIndex: state.currentStepIndex + 1,
      };

    case 'PREVIOUS_STEP':
      return {
        ...state,
        currentStepIndex: Math.max(0, state.currentStepIndex - 1),
      };

    case 'SKIP_TUTORIAL':
      return {
        ...state,
        isActive: false,
        isComplete: true,
        hasBeenSkipped: true,
        showIntro: false,
      };

    case 'COMPLETE_TUTORIAL':
      return {
        ...state,
        isActive: false,
        isComplete: true,
        hasBeenCompleted: true,
      };

    case 'RESTART_TUTORIAL':
      return {
        ...state,
        isActive: true,
        currentStepIndex: 0,
        isComplete: false,
        showIntro: false,
      };

    case 'RESTART_AT_STEP':
      return {
        ...state,
        isActive: true,
        currentStepIndex: action.stepIndex,
        isComplete: false,
        showIntro: false,
      };

    default:
      return state;
  }
}

export function useWarrantyTutorial() {
  const [state, dispatch] = useReducer(tutorialReducer, initialState);

  const startTutorial = useCallback((stepIndex?: number) => {
    dispatch({ type: 'START_TUTORIAL', stepIndex });
  }, []);

  const nextStep = useCallback(() => {
    dispatch({ type: 'NEXT_STEP' });
  }, []);

  const previousStep = useCallback(() => {
    dispatch({ type: 'PREVIOUS_STEP' });
  }, []);

  const skipTutorial = useCallback(() => {
    dispatch({ type: 'SKIP_TUTORIAL' });
  }, []);

  const completeTutorial = useCallback(() => {
    dispatch({ type: 'COMPLETE_TUTORIAL' });
  }, []);

  const restartTutorial = useCallback(() => {
    dispatch({ type: 'RESTART_TUTORIAL' });
  }, []);

  const restartAtStep = useCallback((stepIndex: number) => {
    dispatch({ type: 'RESTART_AT_STEP', stepIndex });
  }, []);

  const showIntro = useCallback(() => {
    dispatch({ type: 'SHOW_INTRO' });
  }, []);

  const hideIntro = useCallback(() => {
    dispatch({ type: 'HIDE_INTRO' });
  }, []);

  return {
    state,
    startTutorial,
    nextStep,
    previousStep,
    skipTutorial,
    completeTutorial,
    restartTutorial,
    restartAtStep,
    showIntro,
    hideIntro,
  };
}
